// ─── src/hooks/useMeetupEvents.ts ─────────────────────────────────────────────

import { useMutation, useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import {
  deleteMeetupEvent,
  getMeetupEvents,
  updateMeetupEvent,
  type UpdateMeetupEventPayload,
} from "../api/meetupEvents";
import type { MeetupEventParams } from "../types/meetupEvent";

export const meetupEventKeys = {
  all:  ["meetupEvents"] as const,
  list: (p: MeetupEventParams) => [...meetupEventKeys.all, "list", p] as const,
};

export const useMeetupEvents = (params: MeetupEventParams) =>
  useQuery({
    queryKey:        meetupEventKeys.list(params),
    queryFn:         () => getMeetupEvents(params),
    placeholderData: keepPreviousData,
    staleTime:       30_000,
  });

export const useDeleteMeetupEvent = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteMeetupEvent(id),
    onSuccess:  () => qc.invalidateQueries({ queryKey: meetupEventKeys.all }),
  });
};

/**
 * Admin edit for a published meetup event. Invalidates both the events list
 * (title/capacity/etc. shown in pickers) and the attendees cache — a capacity
 * bump on the backend triggers waitlist promotions, so those rows can shift.
 * Uses the literal "meetupAttendees" root to sidestep the circular import
 * with useMeetupAttendees.
 */
export const useUpdateMeetupEvent = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateMeetupEventPayload }) =>
      updateMeetupEvent(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: meetupEventKeys.all });
      qc.invalidateQueries({ queryKey: ["meetupAttendees"] });
    },
  });
};
